import { useEffect, useRef, useState } from 'react';
import { useSocket } from '../context/WebsocketContext';

// Mirror the server TOTAL_RECEIVED_BOOST_CAP; a display-only clamp for the
// optimistic bump (the exact tapered value settles on the next getUser hydration).
const VOUCH_BOOST_CAP = 25;

export interface ReputationLike {
  source: string;
  overallScore?: number;
  vouchBoost?: number;
  domains?: string[];
  dimensionScores?: Record<string, number>;
  dimensionConfidences?: Record<string, number>;
  evidence?: Record<string, string[]>;
}

interface ReputationUpdatedPayload {
  userId?: string;
  reputation?: ReputationLike;
}

interface VouchReceivedPayload {
  recipientUserId?: string;
  boost?: number;
}

/**
 * Returns `initial`, overridden live when a `reputation.updated` WS event
 * arrives for `userId` (TRIL-346). The event fires server-side on publish,
 * reply, moderation verdict, and profile update, so a profile's shown score
 * stays current without a reload. Returns `initial` untouched when there's no
 * socket event or the event is for a different user.
 */
export function useLiveReputation(
  userId: string | null | undefined,
  initial: ReputationLike | null | undefined,
): ReputationLike | null | undefined {
  const { onEvent } = useSocket();
  const [live, setLive] = useState<ReputationLike | null>(null);
  // Ref so the vouch_received handler reads the latest `initial` without
  // putting it in the effect deps (which would resubscribe every render).
  const initialRef = useRef(initial);
  initialRef.current = initial;

  // Drop any live override when the viewed user changes (e.g. navigating
  // between profiles) so we never show one user's score under another.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed on userId only
  useEffect(() => {
    setLive(null);
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const off = onEvent(
      'reputation.updated',
      (payload: ReputationUpdatedPayload) => {
        if (payload?.userId === userId && payload.reputation) {
          const incoming = payload.reputation;
          setLive((prev) => ({
            ...incoming,
            // reputation.updated carries the authoritative earned score but no
            // vouchBoost (it's resolver-computed, never stored) — preserve the
            // shown boost so the caption doesn't flicker off on publish/reply.
            vouchBoost:
              incoming.vouchBoost ??
              prev?.vouchBoost ??
              initialRef.current?.vouchBoost,
          }));
        }
      },
    );
    // TRIL-403: vouching never fires reputation.updated (the stored score is
    // untouched), so optimistically bump the displayed vouchBoost when a vouch
    // lands for this user (capped; exact value settles on next hydration).
    const offVouch = onEvent(
      'vouch_received',
      (payload: VouchReceivedPayload) => {
        if (payload?.recipientUserId !== userId || !payload?.boost) return;
        setLive((prev) => {
          const base = prev ?? initialRef.current;
          if (!base) return prev;
          const nextBoost = Math.min(
            VOUCH_BOOST_CAP,
            (base.vouchBoost ?? 0) + payload.boost!,
          );
          return { ...base, vouchBoost: nextBoost };
        });
      },
    );
    return () => {
      off();
      offVouch();
    };
  }, [userId, onEvent]);

  return live ?? initial;
}
